import { formatInTimeZone } from "date-fns-tz";
import type {
  OptionEstimates,
  SlotDeliveryEstimate,
  WithoutPickupEstimate,
} from "@/api.types.v2";

export interface DeliveryEstimateFormatOptions {
  locale?: string;
  t?: (key: string, options?: Record<string, unknown>) => string;
}

/**
 * Delivery-estimate display formatting.
 *
 * Rules (eng review, Codex finding #22): every rendered estimate is prefixed
 * "Est." / phrased as an estimate — never bare "delivered by Wednesday".
 * Date-precision values are "YYYY-MM-DD" strings (never ISO-midnight — that
 * is exactly the timezone date-shift bug these helpers exist to avoid).
 *
 * Time style matches `formatSlotRangeInTz` (dispatch-time.ts) so estimate
 * lines and slot ranges read consistently side-by-side:
 *   "1PM", "7:30PM" (compact), ranges end with the tz abbreviation ("EDT").
 */

function isZhLocale(locale?: string): boolean {
  return locale?.toLowerCase().startsWith("zh") ?? false;
}

function zhDateOptions(
  pattern: string,
  timeZone: string
): Intl.DateTimeFormatOptions {
  return {
    timeZone,
    ...(pattern.includes("EEE")
      ? { weekday: pattern.includes("EEEE") ? "long" : "short" }
      : {}),
    ...(pattern.includes("MMM")
      ? { month: pattern.includes("MMMM") ? "long" : "short" }
      : {}),
    ...(pattern.includes("d") ? { day: "numeric" } : {}),
    ...(pattern.includes("yyyy") ? { year: "numeric" } : {}),
  };
}

function translate(
  options: DeliveryEstimateFormatOptions | undefined,
  key: string,
  fallback: string,
  values: Record<string, string> = {}
): string {
  const i18nKey = `createShipment.deliveryEstimate.${key}`;
  const translated = options?.t?.(i18nKey, values);
  if (translated && translated !== i18nKey) return translated;
  return fallback.replace(/\{\{(\w+)\}\}/g, (_, name) => values[name] ?? "");
}

function formatLocalizedDate(
  date: Date,
  timeZone: string,
  pattern: string,
  options?: DeliveryEstimateFormatOptions
): string {
  if (!isZhLocale(options?.locale)) {
    return formatInTimeZone(date, timeZone, pattern);
  }
  return new Intl.DateTimeFormat(
    "zh-CN",
    zhDateOptions(pattern, timeZone)
  ).format(date);
}

/** "2026-06-03" → "Wed, Jun 3" (pure calendar date, no timezone involved). */
export function formatEstimateDate(
  date: string,
  options?: DeliveryEstimateFormatOptions
): string {
  // Anchor at noon UTC so no real-world timezone can shift the calendar date.
  return formatLocalizedDate(
    new Date(`${date}T12:00:00Z`),
    "UTC",
    "EEE, MMM d",
    options
  );
}

/** Compact time matching formatSlotRangeInTz: "1PM", "7:30PM". */
function compactTime(
  iso: string,
  timeZone: string,
  options?: DeliveryEstimateFormatOptions
): string {
  const h = Number(formatInTimeZone(new Date(iso), timeZone, "H"));
  const m = Number(formatInTimeZone(new Date(iso), timeZone, "m"));
  if (isZhLocale(options?.locale)) {
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
  }
  const period = h >= 12 ? "PM" : "AM";
  const dh = h % 12 || 12;
  return m === 0
    ? `${dh}${period}`
    : `${dh}:${String(m).padStart(2, "0")}${period}`;
}

/** Timezone abbreviation at the instant ("EDT" / "EST"). */
function tzAbbr(iso: string, timeZone: string): string {
  return formatInTimeZone(new Date(iso), timeZone, "zzz");
}

/** "Wed, Jun 3" of an ISO instant in the dispatch tz. */
function dayOf(
  iso: string,
  timeZone: string,
  options?: DeliveryEstimateFormatOptions
): string {
  return formatLocalizedDate(new Date(iso), timeZone, "EEE, MMM d", options);
}

/** Weekday word: "today" when the instant is today (dispatch-local), else "Wed". */
function dayWord(
  iso: string,
  timeZone: string,
  options?: DeliveryEstimateFormatOptions
): string {
  return isTodayInTz(iso, timeZone)
    ? translate(options, "today", "today")
    : formatLocalizedDate(new Date(iso), timeZone, "EEE", options);
}

function isTodayInTz(iso: string, timeZone: string): boolean {
  return (
    formatInTimeZone(new Date(iso), timeZone, "yyyy-MM-dd") ===
    formatInTimeZone(new Date(), timeZone, "yyyy-MM-dd")
  );
}

function sameLocalDay(a: string, b: string, timeZone: string): boolean {
  return (
    formatInTimeZone(new Date(a), timeZone, "yyyy-MM-dd") ===
    formatInTimeZone(new Date(b), timeZone, "yyyy-MM-dd")
  );
}

/**
 * Bare delivered-range text (no "Est." prefix), compact + tz-suffixed:
 *   date precision, same day:    "Wed, Jun 3"
 *   date precision, range:       "Wed, Jun 3 – Thu, Jun 4"
 *   datetime, same evening:      "Wed, Jun 3, 7PM – 11PM EDT"
 *   datetime, spans days:        "Wed, Jun 3, 7PM – Thu, Jun 4, 11PM EDT"
 * Accepts anything carrying a delivered range (slot estimates and the
 * option-level withPickup line share this shape).
 */
function deliveredRangeText(
  estimate: Pick<SlotDeliveryEstimate, "delivered" | "deliveredPrecision">,
  timeZone: string,
  options?: DeliveryEstimateFormatOptions
): string {
  const { fastest, slowest } = estimate.delivered;
  if (estimate.deliveredPrecision === "date") {
    return fastest === slowest
      ? formatEstimateDate(fastest, options)
      : `${formatEstimateDate(fastest, options)} – ${formatEstimateDate(
          slowest,
          options
        )}`;
  }
  // datetime precision (EZX local delivery)
  if (sameLocalDay(fastest, slowest, timeZone)) {
    return `${dayOf(fastest, timeZone, options)}, ${compactTime(
      fastest,
      timeZone,
      options
    )} – ${compactTime(slowest, timeZone, options)} ${tzAbbr(
      slowest,
      timeZone
    )}`;
  }
  return `${dayOf(fastest, timeZone, options)}, ${compactTime(
    fastest,
    timeZone,
    options
  )} – ${dayOf(slowest, timeZone, options)}, ${compactTime(
    slowest,
    timeZone,
    options
  )} ${tzAbbr(slowest, timeZone)}`;
}

/** The delivered range of a slot estimate, "Est." prefixed. */
export function formatDeliveredRange(
  estimate: SlotDeliveryEstimate,
  timeZone: string,
  options?: DeliveryEstimateFormatOptions
): string {
  return translate(options, "estimatedDelivery", "Est. delivery {{range}}", {
    range: deliveredRangeText(estimate, timeZone, options),
  });
}

/** Short per-slot annotation for the slot picker tiles (no tz suffix — the
 *  slot range right above it already shows it). */
export function formatSlotEstimateShort(
  estimate: SlotDeliveryEstimate,
  timeZone: string,
  options?: DeliveryEstimateFormatOptions
): string {
  const { fastest, slowest } = estimate.delivered;
  if (estimate.deliveredPrecision === "date") {
    return fastest === slowest
      ? translate(options, "estimatedDelivery", "Est. delivery {{range}}", {
          range: formatEstimateDate(fastest, options),
        })
      : translate(options, "estimatedRange", "Est. {{range}}", {
          range: `${formatEstimateDate(fastest, options)} – ${formatEstimateDate(
            slowest,
            options
          )}`,
        });
  }
  if (sameLocalDay(fastest, slowest, timeZone)) {
    return translate(options, "estimatedDelivery", "Est. delivery {{range}}", {
      range: `${dayOf(fastest, timeZone, options)} ${compactTime(
        fastest,
        timeZone,
        options
      )} – ${compactTime(slowest, timeZone, options)}`,
    });
  }
  return translate(options, "estimatedDelivery", "Est. delivery {{range}}", {
    range: `${dayOf(fastest, timeZone, options)} ${compactTime(
      fastest,
      timeZone,
      options
    )} – ${dayOf(slowest, timeZone, options)} ${compactTime(
      slowest,
      timeZone,
      options
    )}`,
  });
}

/** At-carrier range for the timeline, same compact style as the pickup row. */
export function formatAtCarrierRange(
  estimate: SlotDeliveryEstimate,
  timeZone: string,
  options?: DeliveryEstimateFormatOptions
): string | null {
  if (!estimate.atCarrier) return null;
  const { fastest, slowest } = estimate.atCarrier;
  if (sameLocalDay(fastest, slowest, timeZone)) {
    return `${dayOf(fastest, timeZone, options)}, ${compactTime(
      fastest,
      timeZone,
      options
    )} – ${compactTime(slowest, timeZone, options)} ${tzAbbr(
      slowest,
      timeZone
    )}`;
  }
  return `${dayOf(fastest, timeZone, options)}, ${compactTime(
    fastest,
    timeZone,
    options
  )} – ${dayOf(slowest, timeZone, options)}, ${compactTime(
    slowest,
    timeZone,
    options
  )} ${tzAbbr(slowest, timeZone)}`;
}

/** Delivered range for the timeline row (no "Est." prefix — the card header
 *  and footnote already establish everything is an estimate). */
export function formatDeliveredRangeForTimeline(
  estimate: SlotDeliveryEstimate,
  timeZone: string,
  options?: DeliveryEstimateFormatOptions
): string {
  return deliveredRangeText(estimate, timeZone, options);
}

/**
 * The option-card placeholder line. `includePickup` decides which conditional
 * shows (the customer hasn't necessarily decided yet — match the toggle).
 * Null = nothing to show (degrade to the plain "N business days" text).
 */
export function formatOptionEstimateLine(
  estimates: OptionEstimates | null,
  includePickup: boolean,
  timeZone: string | null,
  options?: DeliveryEstimateFormatOptions
): string | null {
  if (!estimates) return null;
  const tz = timeZone ?? "America/Toronto";

  if (includePickup) {
    const wp = estimates.withPickup;
    if (!wp) return null;
    // Always name the day — "by 12PM" alone is ambiguous when the earliest
    // bookable slot is tomorrow (the exact bug from manual verification).
    const bookDay = dayOf(wp.bookByTime, tz, options);
    // Full fastest–slowest range, not just the optimistic bound — a single
    // datetime reads like a promise.
    return translate(
      options,
      "bookPickup",
      "Book pickup {{day}} by {{time}} -> est. delivery {{delivery}}",
      {
        day: bookDay,
        time: compactTime(wp.bookByTime, tz, options),
        delivery: deliveredRangeText(wp, tz, options),
      }
    );
  }

  return formatWithoutPickupLine(
    estimates.withoutPickup,
    estimates.withoutPickup?.timeZone ?? tz,
    options
  );
}

function formatWithoutPickupLine(
  wop: WithoutPickupEstimate | null,
  timeZone: string,
  options?: DeliveryEstimateFormatOptions
): string | null {
  if (!wop) return null;
  if (wop.handoff.type === "drop_at_carrier") {
    return translate(
      options,
      "dropOffToday",
      "Drop off today -> est. delivery {{date}}",
      { date: deliveredRangeText(wop, timeZone, options) }
    );
  }
  // bring_to_dispatch (EZX local)
  const bringDay = dayWord(wop.handoff.handoffBy, timeZone, options);
  const deliveredDay = dayWord(wop.delivered.fastest, timeZone, options);
  return translate(
    options,
    "bringToUs",
    "Bring to us {{day}} by {{time}} -> est. delivery {{deliveryDay}} {{startTime}} - {{endTime}}",
    {
      day: bringDay,
      time: compactTime(wop.handoff.handoffBy, timeZone, options),
      deliveryDay: deliveredDay,
      startTime: compactTime(wop.delivered.fastest, timeZone, options),
      endTime: compactTime(wop.delivered.slowest, timeZone, options),
    }
  );
}
